import React, { useEffect, useState, useCallback } from 'react';
import { Create, useForm } from '@refinedev/antd';
import { Form, Input, InputNumber, Select, Switch, Row, Col, Card, message } from 'antd';
import { IResourceComponentsProps, useList, useNavigation } from '@refinedev/core';
import { axiosInstance } from '@/services/api';
import { BaseStatsFormSection } from './BaseStatsFormSection';
import { syncPlayerTypeAndRole } from './syncPlayerTypeAndRole';

export const PlayerDataCreate: React.FC<IResourceComponentsProps> = () => {
  const { formProps, saveButtonProps, form } = useForm();
  const { list } = useNavigation();
  const [includeBaseStats, setIncludeBaseStats] = useState(false);
  const [saving, setSaving] = useState(false);

  const { data: playerListData } = useList({
    resource: 'player-data',
    pagination: { current: 1, pageSize: 1 },
  });

  useEffect(() => {
    if (playerListData?.total != null) {
      form?.setFieldsValue({ playerIndex: playerListData.total });
    }
  }, [playerListData, form]);

  const handleValuesChange = useCallback(
    (changed: Record<string, unknown>) => {
      if (!form || !includeBaseStats) return;
      syncPlayerTypeAndRole(form, changed);
    },
    [form, includeBaseStats]
  );

  const handleFinish = async (values: Record<string, unknown>) => {
    setSaving(true);
    try {
      const masterData: Record<string, unknown> = {};
      const baseStatsData: Record<string, unknown> = {};

      for (const [key, value] of Object.entries(values)) {
        if (key.startsWith('baseStats_')) {
          if (value != null && value !== '') baseStatsData[key.replace('baseStats_', '')] = value;
        } else {
          masterData[key] = value;
        }
      }

      // accField is decimal — ensure it's sent as a number, not a string
      if (baseStatsData.accField != null) {
        baseStatsData.accField = Number(baseStatsData.accField);
      }

      const response = await axiosInstance.post('/admin/player-data', masterData);
      const playerIndex = response.data.playerIndex;

      if (includeBaseStats) {
        baseStatsData.playerIndex = playerIndex;
        await axiosInstance.post(`/admin/player-data/${playerIndex}/base-stats`, baseStatsData);
      }

      void message.success('Player created successfully');
      list('player-data');
    } catch (err: unknown) {
      const error = err as { response?: { data?: { message?: string } } };
      void message.error(error?.response?.data?.message || 'Failed to create player');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Create
      saveButtonProps={{
        ...saveButtonProps,
        loading: saving,
        onClick: () => form?.submit(),
      }}
    >
      <Form
        {...formProps}
        layout="vertical"
        onFinish={handleFinish}
        onValuesChange={handleValuesChange}
      >
        <Card title="Player Identity" style={{ marginBottom: 16 }}>
          <Row gutter={16}>
            <Col xs={24} sm={8}>
              <Form.Item label="Player Index" name="playerIndex">
                <InputNumber disabled style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item label="First Name" name="firstName" rules={[{ required: true }]}>
                <Input maxLength={50} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item label="Second Name" name="secondName">
                <Input maxLength={50} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} sm={8}>
              <Form.Item label="Display Name" name="displayName" rules={[{ required: true }]}>
                <Input maxLength={100} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item label="Nationality" name="nationality">
                <Input maxLength={50} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item label="Team Name" name="teamName">
                <Input maxLength={50} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} sm={6}>
              <Form.Item
                label="Team Index"
                name="teamIndex"
                rules={[{ required: true }]}
                initialValue={0}
              >
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={6}>
              <Form.Item
                label="Jersey Number"
                name="jerseyNumber"
                rules={[{ required: true }]}
                initialValue={0}
              >
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={6}>
              <Form.Item
                label="Face Asset ID"
                name="faceAssetId"
                rules={[{ required: true }]}
                initialValue={0}
              >
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={6}>
              <Form.Item label="Sort Order" name="sortOrder" initialValue={0}>
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} sm={6}>
              <Form.Item
                label="Player Type"
                name="defaultPlayerType"
                rules={[{ required: true }]}
                initialValue={0}
              >
                <Select
                  options={[
                    { label: 'Batsman', value: 0 },
                    { label: 'Bowler', value: 1 },
                    { label: 'All-Rounder', value: 2 },
                  ]}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={6}>
              <Form.Item
                label="Bowler Type"
                name="defaultBowlerType"
                rules={[{ required: true }]}
                initialValue={-1}
              >
                <Select
                  options={[
                    { label: 'Non-Bowler', value: -1 },
                    { label: 'Fast', value: 0 },
                    { label: 'Off Break', value: 1 },
                    { label: 'Leg Break', value: 2 },
                    { label: 'Medium', value: 3 },
                  ]}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={6}>
              <Form.Item
                label="Batting Hand"
                name="battingHand"
                rules={[{ required: true }]}
                initialValue={0}
              >
                <Select
                  options={[
                    { label: 'Right', value: 0 },
                    { label: 'Left', value: 1 },
                  ]}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={6}>
              <Form.Item
                label="Bowling Hand"
                name="bowlingHand"
                rules={[{ required: true }]}
                initialValue={0}
              >
                <Select
                  options={[
                    { label: 'Right', value: 0 },
                    { label: 'Left', value: 1 },
                  ]}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} sm={6}>
              <Form.Item
                label="Bowling Action"
                name="bowlingAction"
                rules={[{ required: true }]}
                initialValue={0}
              >
                <Select
                  options={[
                    { label: 'Normal', value: 0 },
                    { label: 'Side-arm', value: 1 },
                    { label: 'Round-arm', value: 2 },
                  ]}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={6}>
              <Form.Item label="Role" name="role">
                <Select
                  allowClear
                  options={[
                    { label: 'Captain', value: 'Captain' },
                    { label: 'Vice-Captain', value: 'Vice-Captain' },
                    { label: 'Player', value: 'Player' },
                  ]}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={6}>
              <Form.Item
                label="Wicket Keeper"
                name="isWicketKeeper"
                valuePropName="checked"
                initialValue={false}
              >
                <Switch />
              </Form.Item>
            </Col>
            <Col xs={24} sm={6}>
              <Form.Item label="Active" name="isActive" valuePropName="checked" initialValue={true}>
                <Switch />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} sm={8}>
              <Form.Item
                label="Min Build Version"
                name="minBuildVersion"
                tooltip="Hide this player from builds older than this. Leave as 1 to show it to every build. Player masters are gated by build number rather than config version, because cards players already own look their master up by index."
              >
                <InputNumber min={1} style={{ width: '100%' }} placeholder="1" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item
                label="Max Build Version"
                name="maxBuildVersion"
                tooltip="Hide this player from builds newer than this. Leave empty for no upper limit."
              >
                <InputNumber min={1} style={{ width: '100%' }} placeholder="No limit" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} sm={8}>
              <Form.Item label="Body Asset ID" name="bodyAssetId">
                <Input maxLength={100} />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        <Card
          title="Base Stats"
          style={{ marginBottom: 16 }}
          extra={
            <Switch
              checked={includeBaseStats}
              onChange={setIncludeBaseStats}
              checkedChildren="On"
              unCheckedChildren="Off"
            />
          }
        >
          {includeBaseStats ? (
            <BaseStatsFormSection />
          ) : (
            <div style={{ color: '#999', textAlign: 'center', padding: 16 }}>
              Toggle on to add base stats for this player
            </div>
          )}
        </Card>
      </Form>
    </Create>
  );
};
